// ── The browser tab's icon: a dot in the open page's colour (report 17b55236) ────────────────────────────────────────
// Chrome, Edge and Firefox won't let a page colour its own tab, so the tab's small icon carries the colour instead: a dot
// in the colour of the open page's top-menu button (tabColor, src/tabPalette.ts), where the logo was. setTabDot(colour)
// draws that dot on a 32×32 canvas and points every icon link of the page (public/index.html: favicon.ico,
// favicon-32.png, favicon-16.png) at it, so whichever one the browser picks shows the dot; setTabDot() with neither a
// colour nor a ring gives each link back the address it had, and the logo comes back. The apple-touch-icon and the
// manifest's icons (a home-screen or installed app's) are left as they are. App.tsx's DocumentTitle calls it for every
// page but two that know more than the address says: the Portal for the tab it has open (Portal/index.tsx: TabDot) and
// Quick Launch for the page it has open (FocusDashboard).
// Report 07ffc4c6: "Add a green-bordered circle to the browser tab for the user page with a white background inside …
// Do the same with Bugify and SLF Property, etc. (Any bubbles that look like the below)" — a page with no top-menu
// colour had no dot to draw and fell back to the logo, so the tabs of the User page, Bugify, SLF Property and every
// other ≡-menu module were told apart by nothing. Those pages do have a look of their own in the menus, a green line on
// white (pagePill in FocusDashboard, report 020e6911), and their tab now carries it: a white dot with a green ring. The
// logo is left where no page is open at all — the Portal before a tab is picked, a dashboard while it loads — and for
// the pages that are a bubble in no menu: sign-in, the public client pages and Admin.

const SIZE = 32;
const RING_W = 3;        // the green ring's line in the 32px square, so about 1.5px at the 16px icon a tab usually shows
const RING = 'ring';     // the ringed dot's key in `drawn`, where a solid dot's key is its colour; no colour is spelt so
// --accent written out: a canvas takes colours, not CSS variables (as ACCENT_WASH in UserPage does for --accent-bg).
// The same value as the variable in global.css, and as the Bank box's button colour.
const ACCENT = '#1D9E75';
const own = new WeakMap<HTMLLinkElement, string | null>();   // each icon link's address before its first dot: the logo
const drawn = new Map<string, string>();                      // dot key → that dot, as a PNG data URL
let showing: string | undefined;                              // the key of the dot on the tab now; undefined = the logo

// The dot, 2px inside the square's edges: filled with `color`, or — no `color`, a page with no top-menu colour — white
// inside a ring in the app's green. undefined where the browser can't draw one.
function dotUrl(color?: string): string | undefined {
  const key = color || RING;
  let url = drawn.get(key);
  if (url) return url;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return undefined;
  const edge = SIZE / 2 - 2;
  ctx.beginPath();
  // A stroke straddles its path, so the ringed dot's circle runs half a line inside the solid dot's edge: the two fill
  // the same circle and the dots on two tabs are the same size.
  ctx.arc(SIZE / 2, SIZE / 2, color ? edge : edge - RING_W / 2, 0, 2 * Math.PI);
  ctx.fillStyle = color || '#FFFFFF';
  ctx.fill();
  if (!color) {
    ctx.lineWidth = RING_W;
    ctx.strokeStyle = ACCENT;
    ctx.stroke();
  }
  url = canvas.toDataURL('image/png');
  drawn.set(key, url);
  return url;
}

// `color`: the open page's top-menu colour (tabColor), undefined for a page that has none. `ring`: put up the ringed dot
// where there is no colour, instead of the logo — for a caller that knows a page is open (report 07ffc4c6, above).
export function setTabDot(color?: string, ring = false): void {
  const key = color || (ring ? RING : undefined);
  const url = key ? dotUrl(color) : undefined;   // undefined: the logo
  const want = url ? key : undefined;
  if (want === showing) return;
  document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]').forEach(link => {
    if (!own.has(link)) own.set(link, link.getAttribute('href'));
    const href = url ?? own.get(link);
    if (href == null) link.removeAttribute('href'); else link.setAttribute('href', href);
  });
  showing = want;
}
